<div
  class="mx-auto flex max-w-3xl flex-col px-4 sm:px-6 lg:px-8"
  [style.height]="ticket() && !loading() ? threadViewportHeight : null"
  [class.py-8]="loading() || !ticket()"
  [class.py-3]="!!ticket() && !loading()">
  @if (!accessToken()) {
    <button
      type="button"
      (click)="goBack()"
      class="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-secondary hover:text-primary">
      <svg class="size-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
      </svg>
      Back to support
    </button>
  }

  @if (loading()) {
    <div class="mt-6 space-y-4" aria-busy="true" aria-label="Loading ticket">
      <div class="h-36 animate-pulse rounded-2xl border border-gray-100 dark:border-zinc-800 bg-gray-50 dark:bg-zinc-800"></div>
      <div class="h-28 animate-pulse rounded-2xl border border-gray-100 dark:border-zinc-800 bg-gray-50 dark:bg-zinc-800"></div>
      <div class="ml-8 h-28 animate-pulse rounded-2xl border border-gray-100 dark:border-zinc-800 bg-gray-50 dark:bg-zinc-800"></div>
    </div>
  } @else if (!ticket()) {
    <div class="mt-6 rounded-2xl border border-red-100 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-6 py-8 text-center">
      <p class="text-sm font-medium text-red-800 dark:text-red-300">{{ error() || 'Ticket not found.' }}</p>
      @if (!accessToken()) {
        <button type="button" (click)="goBack()" class="mt-4 text-sm font-semibold text-red-700 hover:underline dark:text-red-300">
          Return to support
        </button>
      }
    </div>
  } @else {
    <div class="mt-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-sm">
      <div class="shrink-0 border-b border-gray-100 dark:border-zinc-800 bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-800 px-6 py-4 sm:px-8">
        <div class="flex items-center justify-between gap-3">
          <p class="text-xs font-semibold uppercase tracking-wide text-primary">Support ticket</p>
          <div class="flex shrink-0 flex-wrap items-center justify-end gap-2">
            @if (!isClosed()) {
              <button
                type="button"
                (click)="closeTicket()"
                [disabled]="submitting()"
                class="btn-basic-sm btn-secondary disabled:opacity-50">
                Mark as closed
              </button>
            }
            <span class="rounded-full px-3 py-1 text-xs font-semibold" [class]="statusClass(ticket()!.status)">
              {{ ticket()!.status === 'open' ? 'Open' : 'Closed' }}
            </span>
          </div>
        </div>

        <h1 class="mt-2 text-xl font-semibold text-primary sm:text-2xl">{{ ticket()!.subject }}</h1>
        <p class="mt-1 text-sm text-secondary">#{{ ticket()!.ticketNumber }} · {{ categoryLabel() }}</p>

        @if (!showPreferenceEditor()) {
          <div class="mt-3 flex flex-wrap items-center gap-2">
            <p class="m-0 text-xs text-secondary">Notify via</p>
            @if (alertPreferencePills(ticket()!.alertPreferences).length > 0) {
              @for (pill of alertPreferencePills(ticket()!.alertPreferences); track pill.label) {
                <span class="rounded-full px-2 py-0.5 text-xs font-semibold" [class]="pill.className">
                  {{ pill.label }}
                </span>
              }
            } @else {
              <p class="m-0 text-xs text-secondary">None selected</p>
            }
            <button
              type="button"
              (click)="openPreferenceEditor()"
              class="ml-1 text-xs font-semibold text-primary hover:underline">
              Change
            </button>
          </div>
          @if (preferenceSuccess()) {
            <p class="mt-2 text-xs font-medium text-green-700 dark:text-green-400">{{ preferenceSuccess() }}</p>
          }
        } @else {
          <div class="mt-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-white/80 dark:bg-zinc-900/80 p-4">
            <app-support-alert-preferences-input
              [alertPreferences]="alertPreferences"
              [hasPushDevice]="ticket()!.hasPushDevice ?? false"
              [allowMobileUpdate]="!accessToken()"
              [userMobile]="ticket()!.user?.mobile ?? null"
              [userEmail]="ticket()!.user?.email ?? null"
              (alertPreferencesChange)="onAlertPreferencesChange($event)"
              (mobileSaved)="onMobileSaved($event)"></app-support-alert-preferences-input>

            @if (preferenceError()) {
              <p class="mt-3 text-sm text-red-600">{{ preferenceError() }}</p>
            }

            <div class="mt-4 flex gap-2">
              <button
                type="button"
                (click)="saveAlertPreferences()"
                [disabled]="savingPreferences() || !hasAlertPreference()"
                class="btn-basic-sm btn-primary disabled:opacity-50">
                {{ savingPreferences() ? 'Saving...' : 'Save preferences' }}
              </button>
              <button
                type="button"
                (click)="cancelPreferenceEdit()"
                [disabled]="savingPreferences()"
                class="btn-basic-sm btn-secondary disabled:opacity-50">
                Cancel
              </button>
            </div>
          </div>
        }
      </div>

      <div #messagesContainer class="min-h-0 flex-1 space-y-4 overflow-y-auto bg-gray-50/60 dark:bg-zinc-950/40 px-4 py-4 sm:px-6">
        @if (showThankYou()) {
          <div class="rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/40 px-4 py-3 shadow-sm">
            <div class="flex items-start justify-between gap-3">
              <div class="flex items-start gap-3">
                <div class="flex size-8 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                  <svg class="size-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                  </svg>
                </div>
                <div>
                  <p class="text-sm font-semibold text-green-900 dark:text-green-200">Thank you for contacting us</p>
                  <p class="mt-1 text-sm text-green-800 dark:text-green-300">
                    We've received your message and aim to reply within 24 hours.
                    You can add more detail below anytime.
                  </p>
                </div>
              </div>
              <button
                type="button"
                (click)="dismissThankYou()"
                aria-label="Dismiss"
                class="shrink-0 rounded-md p-1 text-green-700 hover:bg-green-100 hover:text-green-900 dark:text-green-300 dark:hover:bg-green-900">
                <svg class="size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        }

        @if (ticket()!.messages.length === 0) {
          <div class="rounded-xl border border-dashed border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-6 py-10 text-center">
            <p class="text-sm text-secondary">No messages yet.</p>
          </div>
        } @else {
          @for (message of ticket()!.messages; track message.id) {
            <div class="flex gap-3" [class]="messageRowClass(message.authorType)">
              @if (message.authorType === 'admin') {
                <div class="flex size-9 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">
                  SH
                </div>
              }

              <div class="max-w-[85%] sm:max-w-[75%]">
                <div class="rounded-2xl px-4 py-3 shadow-sm" [class]="messageBubbleClass(message.authorType)">
                  <div class="flex flex-wrap items-center justify-between gap-2">
                    <p class="text-xs font-semibold" [class]="messageAuthorClass(message.authorType)">
                      {{ message.authorType === 'admin' ? 'SHOUT Support' : 'You' }}
                    </p>
                    <p class="text-xs text-secondary">
                      {{ message.createdDate | date: 'd MMM, h:mm a' }}
                    </p>
                  </div>

                  @if (message.body) {
                    <p class="mt-2 whitespace-pre-wrap text-sm leading-6 text-primary">
                      {{ message.body }}
                    </p>
                  }

                  @if (message.attachments?.length) {
                    <app-support-attachments-display [attachments]="message.attachments || []"></app-support-attachments-display>
                  }
                </div>
              </div>

              @if (message.authorType === 'user') {
                <div class="flex size-9 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-bold text-gray-600 dark:bg-zinc-700 dark:text-zinc-300">
                  You
                </div>
              }
            </div>
          }
        }
        <div #messagesEnd aria-hidden="true"></div>
      </div>

      <div class="shrink-0 border-t border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-4 sm:px-6">
        @if (isClosed()) {
          <div class="mb-3 flex items-start gap-3 rounded-xl border border-amber-100 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 px-4 py-3">
            <svg class="mt-0.5 size-5 shrink-0 text-amber-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
            <div>
              <p class="text-sm font-medium text-amber-900 dark:text-amber-200">This ticket is closed</p>
              <p class="mt-0.5 text-sm text-amber-800 dark:text-amber-300">
                Send a reply below if you need more help — we'll reopen it automatically.
              </p>
            </div>
          </div>
        }

        <label for="reply" class="block text-sm font-semibold text-primary">{{ replyComposerTitle() }}</label>

        <div
          class="mt-2 overflow-hidden rounded-xl border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 shadow-sm focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
          <textarea
            id="reply"
            name="reply"
            rows="3"
            [(ngModel)]="reply"
            [placeholder]="isClosed() ? 'Tell us what you still need help with...' : 'Type your message...'"
            class="block w-full border-0 bg-transparent px-3 py-2.5 text-sm text-primary focus:outline-none focus:ring-0"></textarea>
          <div class="border-t border-gray-100 dark:border-zinc-700 px-2 py-1.5">
            <app-support-attachment-input
              variant="icon"
              [attachments]="attachments"
              (attachmentsChange)="onAttachmentsChange($event)"></app-support-attachment-input>
          </div>
        </div>

        @if (error()) {
          <p class="mt-2 text-sm text-red-600">{{ error() }}</p>
        }

        <div class="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            (click)="sendReply()"
            [disabled]="submitting() || !canSendReply()"
            class="btn-basic-sm btn-primary disabled:opacity-50">
            {{ replyButtonLabel() }}
          </button>
        </div>
      </div>
    </div>
  }
</div>
